import { CustomRichText } from '@/lib/components/core/custom-richtext/CustomRichText';
import { Page } from '@/lib/components/core/pageItems/page';
import { contentRoleOptions } from '@/lib/constants/contentRoles';
import { useCreateArtikel } from '@/lib/queries/content';
import { ArtikelBijlageInput, ArtikelInput } from '@/lib/types/content';
import { normalizeArticleImageUrls } from '@/lib/utils/articleImages';
import { fileToBase64 } from '@/lib/utils/fileToBase64';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Divider, Form, Input, List, Switch, Typography, Upload, message } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const CreateArticlePage: React.FunctionComponent = () => {
  const { Title } = Typography;
  const { t } = useTranslation('core');
  const [form] = useForm<ArtikelInput>();
  const navigate = useNavigate();
  const [nieuweBijlagen, setNieuweBijlagen] = useState<ArtikelBijlageInput[]>([]);
  const [isBestandVerwerken, setIsBestandVerwerken] = useState(false);
  const { mutateAsync: createArtikel } = useCreateArtikel();
  const getTitelVanBestandsNaam = (bestandsNaam: string) => bestandsNaam.replace(/\.pdf$/i, '');
  const normaliseerBijlageTitel = (titel: string) => titel.replace(/\.pdf$/i, '').trim();

  const handleFinish = async (values: ArtikelInput) => {
    const bijlagenMetTitel = nieuweBijlagen.map(bijlage => ({
      ...bijlage,
      title: normaliseerBijlageTitel(bijlage.title),
    }));

    if (bijlagenMetTitel.some(bijlage => !bijlage.title)) {
      message.error(t('articleAttachments.titleRequiredError'));
      return;
    }

    const payload: ArtikelInput = {
      titel: values.titel,
      body: normalizeArticleImageUrls(values.body) ?? '',
      isPubliek: values.isPubliek ?? true,
      rollen: values.isPubliek ? [] : (values.rollen ?? []),
      nieuweBijlagen: bijlagenMetTitel.length > 0 ? bijlagenMetTitel : undefined,
    };

    try {
      await createArtikel(payload);
      navigate('/content-management');
    } catch (error) {
      console.error('Error creating article:', error);
    }
  };

  const verwijderNieuweBijlage = (index: number) => {
    setNieuweBijlagen(current => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleBeforeUpload = async (file: File) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      message.error(t('articleAttachments.pdfOnlyError'));
      return Upload.LIST_IGNORE;
    }

    setIsBestandVerwerken(true);

    try {
      const base64 = await fileToBase64(file);
      setNieuweBijlagen(current => [...current, { title: getTitelVanBestandsNaam(file.name), name: file.name, file: base64 }]);
    } catch (error) {
      console.error('Error reading pdf:', error);
      message.error(t('articleAttachments.uploadReadError'));
    } finally {
      setIsBestandVerwerken(false);
    }

    return Upload.LIST_IGNORE;
  };

  const wijzigBijlageTitel = (index: number, titel: string) => {
    setNieuweBijlagen(current =>
      current.map((bijlage, currentIndex) => {
        if (currentIndex !== index) {
          return bijlage;
        }

        return {
          ...bijlage,
          title: titel,
        };
      })
    );
  };

  return (
    <Page title="Nieuws toevoegen">
      <Form form={form} onFinish={handleFinish} initialValues={{ isPubliek: true, rollen: [] }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
          <Button type="primary" htmlType="submit">
            Invoeren
          </Button>
          <Button onClick={() => navigate('/content-management')}>{t('articleAttachments.cancelButton')}</Button>
        </div>
        <Divider />
        <Title style={{ margin: '10px 0px' }} level={5}>
          Titel
        </Title>

        <Col span={4}>
          <Form.Item name="titel" rules={[{ required: true, message: '' }]}>
            <Input />
          </Form.Item>
        </Col>

        <Title style={{ margin: '10px 0px' }} level={5}>
          Inhoud
        </Title>
        <Form.Item name="body">
          <CustomRichText />
        </Form.Item>

        <Form.Item name="isPubliek" label="Publiek zichtbaar" valuePropName="checked" initialValue={true}>
          <Switch checkedChildren="Ja" unCheckedChildren="Nee" onChange={checked => checked && form.setFieldValue('rollen', [])} />
        </Form.Item>

        <Form.Item shouldUpdate noStyle>
          {() =>
            !form.getFieldValue('isPubliek') && (
              <Form.Item name="rollen" label="Doelgroepen" initialValue={[]} rules={[{ required: true, message: '' }]}>
                <Checkbox.Group options={contentRoleOptions} />
              </Form.Item>
            )
          }
        </Form.Item>

        <Title style={{ margin: '10px 0px' }} level={5}>
          {t('articleAttachments.uploadTitle')}
        </Title>

        <Upload beforeUpload={handleBeforeUpload} showUploadList={false} multiple accept=".pdf">
          <Button icon={<UploadOutlined />} loading={isBestandVerwerken}>
            {t('articleAttachments.addButton')}
          </Button>
        </Upload>

        {nieuweBijlagen.length > 0 && (
          <List
            style={{ margin: '12px 0 24px', maxWidth: 560 }}
            size="small"
            bordered
            header={<Typography.Text strong>{t('articleAttachments.selectedFilesTitle')}</Typography.Text>}
            dataSource={nieuweBijlagen}
            renderItem={(bijlage, index) => (
              <List.Item
                actions={[
                  <Button key={`nieuw-${bijlage.name}-${index}`} type="text" icon={<DeleteOutlined />} onClick={() => verwijderNieuweBijlage(index)} danger>
                    {t('articleAttachments.removeButton')}
                  </Button>,
                ]}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                  <Typography.Text>{bijlage.name}</Typography.Text>
                  <Input
                    size="small"
                    value={bijlage.title}
                    onChange={event => wijzigBijlageTitel(index, event.target.value)}
                    placeholder={t('articleAttachments.titlePlaceholder')}
                    style={{ maxWidth: 320 }}
                  />
                </div>
              </List.Item>
            )}
          />
        )}
      </Form>
    </Page>
  );
};
